import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useNotificacionStore } from '@rfacil/shared/hooks';
import {
  RecuperarPassword,
  ValidateCode,
  RestablecerChangePassword,
  type ContraseniasDTO,
  ClaveRecPSSDTO,
} from '../services/envioCorreoService';

/**
 * Hook para solicitar el envío de instrucciones de recuperación de contraseña al correo indicado (SSO / EnvioCorreo).
 * El resultado incluye `Estatus` y `Descripcion`; la UI decide notificación o pantalla de código según `Estatus`.
 * Errores HTTP/red: el componente puede mostrar `Alert` u otra UI según corresponda.
 */
export function useRecuperarPassword() {
  return useMutation({
    mutationFn: (Correo: string) => RecuperarPassword(Correo),
    onError: (error) => {
      console.error('Error al enviar las instrucciones de recuperación de contraseña:', error);
    },
  });
}

/**
 * Hook para validar la clave/código de recuperación contra el SSO (`POST /Login/ValidarClave`).
 * Devuelve `RespuestaDTO` con `Estatus` y `Descripcion`; la UI decide el siguiente paso.
 */
export function useValidarClave() {
  return useMutation({
    mutationFn: (data: ClaveRecPSSDTO) => ValidateCode(data),
    onError: (error) => {
      console.error('Error al validar la clave de recuperación:', error);
    },
  });
}

/**
 * Restablece la contraseña tras el flujo de recuperación (`POST /Login/RecuperarContrasenia`).
 * Si la API responde `Estatus: false`, se rechaza la mutación con `Descripcion` para mostrar error.
 */
export function useRestablecerContraseniaRecuperacion() {
  const { showNotification } = useNotificacionStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: ContraseniasDTO) => {
      const result = await RestablecerChangePassword(data);
      if (!result.Estatus) {
        throw new Error(
          result.Descripcion?.trim() || 'No se pudo restablecer la contraseña. Intenta de nuevo.'
        );
      }
      return result;
    },
    onSuccess: () => {
      showNotification('Contraseña restablecida. Ya puedes iniciar sesión.', 'success');
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      console.error('Error al restablecer la contraseña (recuperación):', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Error al restablecer la contraseña. Por favor intenta nuevamente.';
      showNotification(message, 'error');
    },
  });
}
